<script setup lang="ts">
import TagDisplay, { type TagNode } from './TagDisplay.vue';
import type { TagIconTheme } from '../lib/icons';
import type { StyleValue } from 'vue';
import { type ColorTint } from '@precon-tools-suite-by-shared/shared-colors-ui';

const props = withDefaults(
    defineProps<{
        nodes: TagNode[];
        hasMore?: boolean;
        moreLoading?: boolean;
        moreLabel?: string;
        name?: 'full' | 'short' | 'none';
        icons?: TagIconTheme | 'none';
        size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
        bgTint?: ColorTint;
        iconTint?: ColorTint;
        textTint?: ColorTint;
        closeIcon?: string;
        closable?: boolean;
        tagClass?: unknown;
        tagStyle?: StyleValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loadingClass?: any;
        loadingStyle?: StyleValue;
    }>(),
    {
        icons: 'pi-normal',
        moreLabel: 'meer...',
        size: 'sm',
        bgTint: '200',
        iconTint: '700',
        textTint: '800',
        closeIcon: 'pi pi-times',
        name: 'short',
        tagClass: () => [],
        tagStyle: () => ({ 'max-width': '15rem' }),
        loadingStyle: () => 'width: 5rem; height: 1.5rem',
        loadingClass: () => [],
    }
);

const emit = defineEmits<{
    (e: 'tag:click', tag: TagNode, index: number, event: MouseEvent): void;
    (e: 'tag:close', tag: TagNode, index: number, event: MouseEvent): void;
    (e: 'more:click', event: MouseEvent): void;
}>();

function onClickMore(event: MouseEvent) {
    if (props.hasMore) {
        emit('more:click', event);
    }
}
</script>

<template>
    <div
        data-cy="tag-list"
        class="flex flex-wrap gap-2 align-content-end align-items-center tag-list"
        :class="{
            'tag-list-has-more': hasMore,
            'tag-list-more-loading': moreLoading,
        }"
    >
        <template v-for="(node, ix) of nodes" :key="node.id">
            <slot name="node" :index="ix" :node="node">
                <TagDisplay
                    :class="tagClass"
                    :style="tagStyle"
                    :node="node"
                    :icons="icons"
                    :size="size"
                    :bg-tint="bgTint"
                    :icon-tint="iconTint"
                    :close-icon="closeIcon"
                    :name="name"
                    :loading-style="loadingStyle"
                    :loading-class="loadingClass"
                    :closable="closable"
                    @click="(e) => emit('tag:click', node, ix, e)"
                    @close:click="(e) => emit('tag:close', node, ix, e)"
                />
            </slot>
        </template>

        <template v-if="moreLoading">
            <div class="tag-more-loading">
                <slot name="more-loading">
                    <i class="pi pi-spinner pi-spin"></i>
                </slot>
            </div>
        </template>

        <template v-else-if="hasMore">
            <div class="tag-more" @click="onClickMore">
                <slot name="more">
                    <span
                        class="underline font-italic text-500 hover:text-800 cursor-pointer"
                        >{{ moreLabel }}</span
                    >
                </slot>
            </div>
        </template>
    </div>
</template>
